import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@pantry/ui';
import { useMergeGroups, type ProductGroupSummary } from '@pantry/api-client';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface MergeGroupDialogProps {
  householdId: string;
  sourceGroup: ProductGroupSummary;
  allGroups: ProductGroupSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Dialog for merging the source group into a selected target group.
 */
export function MergeGroupDialog({
  householdId,
  sourceGroup,
  allGroups,
  open,
  onOpenChange,
  onSuccess,
}: MergeGroupDialogProps) {
  const { t } = useLingui();
  const [targetGroupId, setTargetGroupId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mergeGroups = useMergeGroups();

  const targetGroup = allGroups.find((g) => g.id === targetGroupId);

  const handleConfirm = () => {
    setError(null);
    if (!targetGroupId) {
      setError(t`Please select a target group`);
      return;
    }
    setConfirmed(true);
  };

  const handleMerge = async () => {
    try {
      await mergeGroups.mutateAsync({
        householdId,
        sourceGroupId: sourceGroup.id,
        targetGroupId,
      });
      toast.success(t`"${sourceGroup.name}" merged into "${targetGroup?.name ?? targetGroupId}"`);
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(t`Failed to merge groups`);
      setConfirmed(false);
    }
  };

  const handleClose = () => {
    setTargetGroupId('');
    setConfirmed(false);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <ArrowsRightLeftIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans context="dialog_title">Merge Group</Trans>
          </DialogTitle>
          <DialogDescription className="pt-1">
            <Trans>Move all members from this group into another group.</Trans>
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-muted/50 border border-border p-4 text-sm text-foreground">
              <Trans>
                All members from &ldquo;{sourceGroup.name}&rdquo; will be moved to &ldquo;
                {targetGroup?.name}&rdquo;. &ldquo;{sourceGroup.name}&rdquo; will be deleted.
              </Trans>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmed(false)}
                disabled={mergeGroups.isPending}
                className="rounded-xl"
              >
                <Trans context="action_button">Back</Trans>
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void handleMerge()}
                disabled={mergeGroups.isPending}
                className="rounded-xl"
              >
                {mergeGroups.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-destructive-foreground/30 border-t-destructive-foreground" />
                    <Trans>Merging…</Trans>
                  </div>
                ) : (
                  <Trans context="action_button">Confirm Merge</Trans>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target-group">
                <Trans>Merge &ldquo;{sourceGroup.name}&rdquo; into…</Trans>
              </Label>
              <select
                id="target-group"
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
              >
                <option value="">{t`Select a group…`}</option>
                {allGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                    {g.category ? ` (${g.category})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleClose} className="rounded-xl">
                <Trans context="action_button">Cancel</Trans>
              </Button>
              <Button type="button" onClick={handleConfirm} className="rounded-xl">
                <Trans context="action_button">Review Merge</Trans>
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
